'use client';
// ──────────────────────────────────────────────────────
// OnScreenKeyboard — Enterprise-grade Arabic keyboard
// Guarantees typing on touch kiosks, no glyph clipping
// ──────────────────────────────────────────────────────
import { useRef, useEffect } from 'react';

interface OnScreenKeyboardProps {
    visible: boolean;
    onKey: (char: string) => void;
    onBackspace: () => void;
    onSpace: () => void;
    onSend: () => void;
    onClose: () => void;
    onHeightChange?: (h: number) => void;
}

const ROWS = [
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'د'],
    ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
    ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ'],
];

export function OnScreenKeyboard({
    visible, onKey, onBackspace, onSpace, onSend, onClose, onHeightChange,
}: OnScreenKeyboardProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    // Report measured height to parent for corridor spacing
    useEffect(() => {
        if (!visible || !containerRef.current || !onHeightChange) return;
        const ro = new ResizeObserver(([entry]) => {
            onHeightChange(entry.contentRect.height + 14); // +14 for borders/margin
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [visible, onHeightChange]);

    if (!visible) return null;

    return (
        <div
            ref={containerRef}
            className="osk-container"
            dir="rtl"
            onPointerDown={e => e.preventDefault()}
        >
            {/* Arabic character rows */}
            {ROWS.map((row, ri) => (
                <div key={ri} className="osk-row">
                    {row.map((ch) => (
                        <button
                            key={ch}
                            type="button"
                            className="osk-key"
                            onPointerDown={e => { e.preventDefault(); onKey(ch); }}
                        >
                            <span className="osk-glyph">{ch}</span>
                        </button>
                    ))}
                </div>
            ))}

            {/* Controls row */}
            <div className="osk-row osk-controls">
                <button
                    type="button"
                    className="osk-key osk-action osk-backspace"
                    onPointerDown={e => { e.preventDefault(); onBackspace(); }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z" />
                        <line x1="18" y1="9" x2="12" y2="15" />
                        <line x1="12" y1="9" x2="18" y2="15" />
                    </svg>
                    <span>مسح</span>
                </button>
                <button
                    type="button"
                    className="osk-key osk-action osk-space"
                    onPointerDown={e => { e.preventDefault(); onSpace(); }}
                >
                    <span>مسافة</span>
                </button>
                <button
                    type="button"
                    className="osk-key osk-action osk-send"
                    onPointerDown={e => { e.preventDefault(); onSend(); }}
                >
                    <span>إرسال</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: 'scaleX(-1)' }}>
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                    </svg>
                </button>
                <button
                    type="button"
                    className="osk-key osk-action osk-close"
                    onPointerDown={e => { e.preventDefault(); onClose(); }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <style>{`
                /* ── Container ────────────────────────── */
                .osk-container {
                    animation: osk-slide-up 0.2s ease-out;
                    border-top: 1px solid #e2e8f0;
                    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
                    padding: 10px 8px 12px;
                    margin-top: 2px;
                    max-height: 42vh;
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                }
                @media (orientation: landscape) {
                    .osk-container { max-height: 36vh; padding: 8px 6px 10px; }
                }

                /* ── Row grid ─────────────────────────── */
                .osk-row {
                    display: flex;
                    justify-content: center;
                    gap: clamp(4px, 0.8vw, 8px);
                    margin-bottom: clamp(4px, 0.6vh, 6px);
                }
                .osk-controls {
                    gap: clamp(6px, 1vw, 10px);
                    margin-top: clamp(4px, 0.8vh, 8px);
                    margin-bottom: 0;
                }

                /* ── Character key ────────────────────── */
                .osk-key {
                    min-width: clamp(30px, 7vw, 42px);
                    height: clamp(44px, 6vh, 56px);
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    background: #ffffff;
                    color: #1a1a2e;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.1s, transform 0.08s, box-shadow 0.1s;
                    user-select: none;
                    -webkit-tap-highlight-color: transparent;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                    padding: 0;
                    outline: none;
                    flex-shrink: 0;
                    position: relative;
                }
                .osk-key:active {
                    transform: scale(0.96);
                    background: #e2e8f0;
                    box-shadow: inset 0 1px 3px rgba(0,0,0,0.08);
                }
                @media (orientation: landscape) {
                    .osk-key {
                        min-width: clamp(28px, 5.5vw, 38px);
                        height: clamp(40px, 5vh, 50px);
                    }
                }

                /* ── Glyph (Arabic char) ──────────────── */
                .osk-glyph {
                    font-family: "Tajawal", "Cairo", system-ui, sans-serif;
                    font-size: clamp(16px, 2.2vw, 20px);
                    font-weight: 600;
                    line-height: 1;
                    letter-spacing: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    padding-bottom: 1px; /* Arabic descender compensation */
                    text-rendering: geometricPrecision;
                    -webkit-font-smoothing: antialiased;
                    overflow: hidden;
                }

                /* ── Action keys ──────────────────────── */
                .osk-action {
                    font-family: "Tajawal", "Cairo", system-ui, sans-serif;
                    font-size: clamp(13px, 1.6vw, 15px);
                    font-weight: 700;
                    gap: 6px;
                    white-space: nowrap;
                    padding: 0 clamp(10px, 1.5vw, 16px);
                }
                .osk-backspace {
                    min-width: clamp(56px, 12vw, 80px);
                    background: #fef2f2;
                    color: #dc2626;
                    border-color: #fecaca;
                }
                .osk-backspace:active { background: #fee2e2; }
                .osk-space {
                    min-width: clamp(90px, 22vw, 140px);
                    background: #ffffff;
                }
                .osk-space:active { background: #f1f5f9; }
                .osk-send {
                    min-width: clamp(64px, 14vw, 96px);
                    background: #0ea5a5;
                    color: #ffffff;
                    border-color: #0d8f8f;
                }
                .osk-send:active { background: #0d8f8f; }
                .osk-close {
                    min-width: clamp(40px, 6vw, 52px);
                    background: #f8fafc;
                    color: #64748b;
                    border-color: #cbd5e1;
                }
                .osk-close:active { background: #e2e8f0; }

                /* ── Animation ────────────────────────── */
                @keyframes osk-slide-up {
                    from { transform: translateY(100%); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
            `}</style>
        </div>
    );
}
